/**
 * Mi Tema — Custom Animation Engine
 * Lightweight IntersectionObserver-based reveal system.
 *
 * Usage:
 *   data-reveal="fade-up"          — animation type
 *   data-reveal-delay="200"        — delay in ms
 *   data-reveal-duration="600"     — override duration
 *   data-reveal-stagger            — on a parent: staggers children
 *   data-reveal-stagger-gap="80"   — ms between each child
 *   data-parallax="0.08"           — subtle parallax factor
 *   data-counter-target="1250"     — counter-up target number
 *
 * Respects prefers-reduced-motion and the theme's reduced_motion setting.
 */
(function () {
  'use strict';

  // ── Config ─────────────────────────────────────
  var DEFAULTS = {
    threshold: 0.15,
    rootMargin: '0px 0px -60px 0px',
    staggerGap: 80,
    duration: 600
  };

  // ── Reduced motion check ───────────────────────
  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var themeReducedMotion =
    document.documentElement.dataset.reducedMotion === 'true';

  if (prefersReducedMotion || themeReducedMotion) {
    // Immediately show all elements without animation
    document.addEventListener('DOMContentLoaded', function () {
      var els = document.querySelectorAll('[data-reveal]');
      for (var i = 0; i < els.length; i++) {
        els[i].classList.add('reveal--visible');
        els[i].style.transitionDuration = '0ms';
      }
    });
    return;
  }

  // ── Reveal Observer ────────────────────────────
  function initReveals() {
    var elements = document.querySelectorAll('[data-reveal]');
    if (!elements.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var el = entry.target;
        var delay = parseInt(el.dataset.revealDelay, 10) || 0;

        setTimeout(function () {
          el.classList.add('reveal--visible');

          // Counter-up animation
          if (el.dataset.reveal === 'counter' && el.dataset.counterTarget) {
            animateCounter(el, parseInt(el.dataset.counterTarget, 10));
          }
        }, delay);

        observer.unobserve(el);
      });
    }, {
      threshold: DEFAULTS.threshold,
      rootMargin: DEFAULTS.rootMargin
    });

    elements.forEach(function (el) {
      // Apply custom duration if specified
      var duration = el.dataset.revealDuration || DEFAULTS.duration;
      el.style.transitionDuration = duration + 'ms';

      observer.observe(el);
    });
  }

  // ── Stagger Observer ───────────────────────────
  function initStaggers() {
    var containers = document.querySelectorAll('[data-reveal-stagger]');
    if (!containers.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var container = entry.target;
        var gap = parseInt(container.dataset.revealStaggerGap, 10) || DEFAULTS.staggerGap;
        var children = container.querySelectorAll('[data-reveal]');

        children.forEach(function (child, index) {
          var baseDelay = parseInt(child.dataset.revealDelay, 10) || 0;
          child.dataset.revealDelay = String(baseDelay + (index * gap));
        });

        observer.unobserve(container);
      });
    }, {
      threshold: 0.05,
      rootMargin: '0px 0px -40px 0px'
    });

    containers.forEach(function (c) { observer.observe(c); });
  }

  // ── Counter Animation ──────────────────────────
  function animateCounter(el, target) {
    var duration = parseInt(el.dataset.revealDuration, 10) || 1200;
    var start = 0;
    var startTime = null;
    var prefix = el.dataset.counterPrefix || '';
    var suffix = el.dataset.counterSuffix || '';
    var decimals = parseInt(el.dataset.counterDecimals, 10) || 0;

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var progress = Math.min((timestamp - startTime) / duration, 1);
      // Ease-out cubic
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = start + (target - start) * eased;

      el.textContent = prefix + current.toFixed(decimals) + suffix;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
  }

  // ── Parallax ───────────────────────────────────
  function initParallax() {
    var elements = document.querySelectorAll('[data-parallax]');
    if (!elements.length) return;

    var ticking = false;

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var scrollY = window.scrollY;
        elements.forEach(function (el) {
          var factor = parseFloat(el.dataset.parallax) || 0.08;
          var rect = el.getBoundingClientRect();
          var elCenter = rect.top + rect.height / 2;
          var viewCenter = window.innerHeight / 2;
          var offset = (elCenter - viewCenter) * factor;
          el.style.transform = 'translateY(' + offset.toFixed(1) + 'px)';
        });
        ticking = false;
      });
    }, { passive: true });
  }

  // ── Init ───────────────────────────────────────
  function init() {
    initStaggers();
    initReveals();
    initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify theme editor section load
  document.addEventListener('shopify:section:load', init);

  // Expose for programmatic use
  window.ThemeAnimations = {
    init: init,
    animateCounter: animateCounter
  };
})();
